import { adminApi } from '@/config/axios.config';
import { Invoice, InvoiceFilters, InvoiceStats, CreateInvoiceDto } from '../types/admin.types';

/**
 * API service para gestión de facturación
 */
export const billingApiService = {
  /**
   * Obtiene todas las facturas
   */
  getAllInvoices: async (filters?: InvoiceFilters): Promise<Invoice[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters?.search) params.append('search', filters.search);
      if (filters?.tenantId) params.append('tenantId', filters.tenantId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await adminApi.get('/billing/invoices', { params });
      return response.data?.data || response.data || [];
    } catch (error) {
      return [];
    }
  },

  /**
   * Obtiene una factura por ID
   */
  getInvoice: async (id: string): Promise<Invoice> => {
    const response = await adminApi.get(`/billing/invoices/${id}`);
    return response.data?.data || response.data;
  },

  /**
   * Obtiene las estadísticas de facturas
   */
  getInvoiceStats: async (): Promise<InvoiceStats> => {
    try {
      const response = await adminApi.get('/billing/invoices/stats');
      return response.data?.data || response.data || {
        total: 0,
        paid: 0,
        pending: 0,
        overdue: 0,
        cancelled: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        overdueAmount: 0,
      };
    } catch (error) {
      return {
        total: 0,
        paid: 0,
        pending: 0,
        overdue: 0,
        cancelled: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        overdueAmount: 0,
      };
    }
  },

  /**
   * Crea una nueva factura
   */
  createInvoice: async (data: CreateInvoiceDto): Promise<Invoice> => {
    const response = await adminApi.post('/billing/invoices', data);
    return response.data?.data || response.data;
  },

  /**
   * Actualiza una factura
   */
  updateInvoice: async (id: string, data: Partial<CreateInvoiceDto>): Promise<Invoice> => {
    const response = await adminApi.patch(`/billing/invoices/${id}`, data);
    return response.data?.data || response.data;
  },

  /**
   * Elimina una factura
   */
  deleteInvoice: async (id: string): Promise<void> => {
    await adminApi.delete(`/billing/invoices/${id}`);
  },

  /**
   * Descarga el PDF de una factura
   */
  downloadInvoicePDF: async (id: string): Promise<Blob> => {
    const response = await adminApi.get(`/billing/invoices/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Marca una factura como pagada
   */
  markAsPaid: async (id: string, paymentMethod?: string): Promise<Invoice> => {
    const response = await adminApi.patch(`/billing/invoices/${id}/pay`, {
      paymentMethod,
    });
    return response.data?.data || response.data;
  },

  /**
   * Obtiene la configuración de impuestos de un tenant
   */
  getTaxConfig: async (tenantId: string): Promise<{
    id: string;
    tenantId: string;
    ivaRate: number;
    ivaEnabled: boolean;
    metadata?: Record<string, any>;
    createdAt: string;
    updatedAt: string;
  }> => {
    try {
      const response = await adminApi.get(`/billing/tax-config/${tenantId}`);
      return response.data?.data || response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener la configuración de impuestos');
    }
  },

  /**
   * Actualiza la configuración de impuestos de un tenant
   */
  updateTaxConfig: async (
    tenantId: string,
    data: {
      ivaRate?: number;
      ivaEnabled?: boolean;
      metadata?: Record<string, any>;
    },
  ): Promise<{
    id: string;
    tenantId: string;
    ivaRate: number;
    ivaEnabled: boolean;
    metadata?: Record<string, any>;
    createdAt: string;
    updatedAt: string;
  }> => {
    try {
      const response = await adminApi.patch(`/billing/tax-config/${tenantId}`, data);
      return response.data?.data || response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al actualizar la configuración de impuestos');
    }
  },
};

